import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box, Typography, TextField, Button, Alert, CircularProgress,
  Divider, FormControl, Select, MenuItem
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import { fieldSx, labelSx, menuProps } from '../styles/formStyles';

const API = process.env.REACT_APP_API_URL || '';

// Umbral único para la experiencia móvil del paciente (Login + Portal) — ver Login.js.
const MOBILE_MQ = '@media (max-width:768px)';
// Altura táctil (44px) solo en mobile, mergeado sobre fieldSx compartido con
// staff (frontend/src/styles/formStyles.js) sin modificar ese archivo.
const fieldSxMobile = [fieldSx, { '& .MuiOutlinedInput-root': { [MOBILE_MQ]: { height: 44 } } }];

const VACIO = {
  nombre: '', apellido: '', dni: '', fecha_nacimiento: '',
  telefono: '', direccion: '', obra_social: '', numero_afiliado: '',
};

function LF({ label, children }) {
  return (
    <Box>
      <Typography sx={labelSx}>{label}</Typography>
      {children}
    </Box>
  );
}

export default function MisDatos({ usuario, onConfirmado }) {
  const navigate = useNavigate();
  const [form, setForm] = useState(VACIO);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const [obrasSociales, setObrasSociales] = useState([]);

  useEffect(() => {
    axios.get(`${API}/api/pacientes/me`, {
      headers: { Authorization: `Bearer ${usuario.token}` }
    }).then(r => {
      const d = r.data;
      setForm({
        nombre:           d.nombre || '',
        apellido:         d.apellido || '',
        dni:              d.dni || '',
        fecha_nacimiento: d.fecha_nacimiento ? String(d.fecha_nacimiento).split('T')[0] : '',
        telefono:         d.telefono || '',
        direccion:        d.direccion || '',
        obra_social:      d.obra_social || '',
        numero_afiliado:  d.numero_afiliado || '',
      });
    }).catch(() => {}).finally(() => setCargando(false));
    axios.get(`${API}/api/obras-sociales`).then(r => setObrasSociales(r.data)).catch(() => {});
  }, [usuario.token]);

  async function guardar() {
    const requeridos = [
      { key: 'nombre',           label: 'Nombre' },
      { key: 'apellido',         label: 'Apellido' },
      { key: 'dni',              label: 'DNI' },
      { key: 'fecha_nacimiento', label: 'Fecha de nacimiento' },
      { key: 'telefono',         label: 'Teléfono' },
    ];
    const faltantes = requeridos.filter(c => !form[c.key]?.trim());
    if (faltantes.length) {
      setError(`Completá los campos obligatorios: ${faltantes.map(c => c.label).join(', ')}`);
      return;
    }
    setGuardando(true);
    setError('');
    try {
      await axios.put(`${API}/api/pacientes/me`, form, {
        headers: { Authorization: `Bearer ${usuario.token}` }
      });
      onConfirmado();
      navigate('/panel-paciente');
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar los datos.');
    } finally {
      setGuardando(false);
    }
  }

  const dniConfirmado = usuario.datos_confirmados;
  const f = field => e => setForm(prev => ({ ...prev, [field]: e.target.value }));

  if (cargando) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
      <CircularProgress sx={{ color: 'var(--c-acento)' }} />
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 760, mx: 'auto', mt: 2, [MOBILE_MQ]: { px: '4px' } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <PersonIcon sx={{ color: 'var(--c-acento)', fontSize: 32 }} />
        <Typography variant="h5" sx={{ fontWeight: 700, color: 'var(--c-acento)' }}>
          {dniConfirmado ? 'Mis datos' : 'Completá tus datos'}
        </Typography>
      </Box>
      {!dniConfirmado && (
        <Typography sx={{ color: '#b0b0b0', mb: 3, fontSize: 14 }}>
          Para continuar necesitamos que confirmes tu información personal.
        </Typography>
      )}

      <Box sx={{ bgcolor: 'var(--c-superficie)', borderRadius: 2, p: 3, border: '1px solid var(--c-primario)' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '16px' }}>

          <LF label="Nombre *">
            <TextField fullWidth value={form.nombre} onChange={f('nombre')}
              inputProps={{ style: { color: '#e0e0e0' } }} sx={fieldSxMobile} />
          </LF>

          <LF label="Apellido *">
            <TextField fullWidth value={form.apellido} onChange={f('apellido')}
              inputProps={{ style: { color: '#e0e0e0' } }} sx={fieldSxMobile} />
          </LF>

          <LF label={dniConfirmado ? 'DNI (no modificable)' : 'DNI *'}>
            <TextField fullWidth value={form.dni}
              onChange={e => !dniConfirmado && f('dni')(e)}
              disabled={dniConfirmado}
              inputProps={{ style: { color: dniConfirmado ? '#78909c' : '#e0e0e0' } }}
              sx={fieldSxMobile} />
          </LF>

          <LF label="Fecha de nacimiento *">
            <TextField type="date" fullWidth value={form.fecha_nacimiento} onChange={f('fecha_nacimiento')}
              inputProps={{ style: { color: '#e0e0e0', colorScheme: 'dark' } }}
              sx={fieldSxMobile} />
          </LF>

          <LF label="Teléfono *">
            <TextField fullWidth value={form.telefono} onChange={f('telefono')}
              inputProps={{ style: { color: '#e0e0e0' } }} sx={fieldSxMobile} />
          </LF>

          <LF label="Dirección">
            <TextField fullWidth value={form.direccion} onChange={f('direccion')}
              inputProps={{ style: { color: '#e0e0e0' } }} sx={fieldSxMobile} />
          </LF>

          <LF label="Obra social">
            <FormControl fullWidth sx={fieldSxMobile}>
              <Select displayEmpty value={form.obra_social} onChange={f('obra_social')}
                sx={{ color: 'var(--c-texto)' }} MenuProps={menuProps}>
                <MenuItem value=""><em style={{ color: '#888' }}>Sin especificar</em></MenuItem>
                {obrasSociales.map(os => (
                  <MenuItem key={os.id} value={os.nombre}>{os.nombre}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </LF>

          <LF label="N° de afiliado">
            <TextField fullWidth value={form.numero_afiliado} onChange={f('numero_afiliado')}
              inputProps={{ style: { color: '#e0e0e0' } }} sx={fieldSxMobile} />
          </LF>

        </Box>

        <Divider sx={{ borderColor: 'var(--c-primario)', my: 2 }} />

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Button variant="contained" onClick={guardar} disabled={guardando} fullWidth
          sx={{
            bgcolor: 'var(--c-primario)', py: 1.2, touchAction: 'manipulation',
            [MOBILE_MQ]: { py: 1.6 },
            '@media (hover: hover)': { '&:hover': { filter: 'brightness(1.2)' } },
          }}>
          {guardando ? 'Guardando...' : 'Confirmar datos'}
        </Button>
      </Box>
    </Box>
  );
}
